import { useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import ReactSelect from 'react-select';
import type { MultiValue, SingleValue, StylesConfig } from 'react-select';
import styled from 'styled-components';
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
} from '@mui/material';
import { DeleteOutline, EditOutlined } from '@mui/icons-material';
import { DarkGreen } from '../../../app/theme/tokens/colors';
import { PrimaryButton, SecondaryButton } from '../../../shared/ui/Inputs';
import { Spinner } from '../../../shared/ui/Spinners';
import { SimplePopup } from '../../../shared/overlays';
import {
  delSapProductBranch,
  listSapProductBranch,
} from '../../../services/configuration/sapProductBranchService';
import { SapProductBranchForm } from './sapProductBranchForm';
import { SapProductBranchUploadModal } from './SapProductBranchUploadModal';

type RecordId = string | number;
type SortOrder = 'asc' | 'desc';
type SortableColumn =
  | 'sap_code'
  | 'branch_id'
  | 'branch_description'
  | 'layout'
  | 'product_id'
  | 'product_description'
  | 'product_generic';
type FilterField = 'sap_code' | 'layout' | 'product_id';

interface ProductBranchRecord {
  sap_code?: string;
  branch_id?: RecordId;
  branch_description?: string;
  layout?: string;
  product_id?: RecordId | null;
  product_description?: string;
  product_generic?: string | boolean;
  [key: string]: unknown;
}

interface SelectOption {
  value: string;
  label: string;
}

interface TableColumn {
  id: 'actions' | SortableColumn;
  label: string;
  minWidth: number;
  sortable: boolean;
  align?: 'center';
}

const ToolBar = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  border-top: solid 1px #b9b9b9;
  padding: 10px 0;
  flex-shrink: 0;
`;

const Content = styled.div`
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const TableWrapper = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const FilterBar = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  flex: 1;
  min-width: 280px;
`;

const FilterBox = styled.div`
  width: 240px;
  min-width: 180px;

  &.filter-value {
    width: 320px;
  }
`;

const FilterLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #555;
`;

const selectStyles: StylesConfig<SelectOption, boolean> = {
  control: (base, state) => ({
    ...base,
    minHeight: '36px',
    borderColor: state.isFocused ? '#4285f4' : '#d9d9d9',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(66,133,244,0.15)' : 'none',
    '&:hover': { borderColor: '#4285f4' },
    borderRadius: '4px',
    fontSize: '13px',
  }),
  placeholder: (base) => ({ ...base, color: '#aaa', fontSize: '13px' }),
  option: (base, state) => ({
    ...base,
    fontSize: '13px',
    backgroundColor: state.isSelected
      ? '#e8f0fe'
      : state.isFocused
      ? '#f8f9fa'
      : 'white',
    color: state.isSelected ? '#1a73e8' : '#333',
  }),
  multiValue: (base) => ({ ...base, backgroundColor: '#eef3ee' }),
  multiValueLabel: (base) => ({ ...base, color: '#2f5d3a', fontSize: '12px' }),
  valueContainer: (base) => ({
    ...base,
    maxHeight: '72px',
    overflowY: 'auto',
    alignContent: 'flex-start',
    paddingTop: '4px',
    paddingBottom: '4px',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const TABLE_COLUMNS: TableColumn[] = [
  {
    id: 'actions',
    label: 'Ações',
    minWidth: 110,
    sortable: false,
    align: 'center',
  },
  { id: 'sap_code', label: 'SAP Code', minWidth: 150, sortable: true },
  { id: 'branch_id', label: 'Branch ID', minWidth: 110, sortable: true },
  { id: 'branch_description', label: 'Filial', minWidth: 180, sortable: true },
  { id: 'layout', label: 'Layout', minWidth: 90, sortable: true },
  { id: 'product_id', label: 'Product ID', minWidth: 120, sortable: true },
  {
    id: 'product_description',
    label: 'Descrição do Produto',
    minWidth: 240,
    sortable: true,
  },
  { id: 'product_generic', label: 'Genérico', minWidth: 110, sortable: true },
];

const FILTER_FIELDS: SelectOption[] = [
  { value: 'sap_code', label: 'SAP Code' },
  { value: 'layout', label: 'Layout' },
  { value: 'product_id', label: 'Product ID' },
];

const normalizeText = (value: unknown): string =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

const formatCellValue = (value: unknown): string =>
  value === null || value === undefined || value === '' ? '—' : String(value);

const getRecordValue = (
  record: ProductBranchRecord,
  column: SortableColumn
): string => formatCellValue(record[column]);

const makeUniqueOptions = (values: SelectOption[]): SelectOption[] => {
  const options = new Map<string, SelectOption>();
  values.forEach((option) => {
    if (!option.value || !option.label || options.has(option.value)) return;
    options.set(option.value, option);
  });
  return Array.from(options.values()).sort((first, second) =>
    first.label.localeCompare(second.label, 'pt-BR', {
      sensitivity: 'base',
      numeric: true,
    })
  );
};

const sortRecords = (
  list: ProductBranchRecord[],
  order: SortOrder,
  orderBy: SortableColumn
): ProductBranchRecord[] =>
  [...list].sort((firstRecord, secondRecord) => {
    const first = normalizeText(getRecordValue(firstRecord, orderBy));
    const second = normalizeText(getRecordValue(secondRecord, orderBy));
    if (!first && !second) return 0;
    if (!first) return 1;
    if (!second) return -1;
    const comparison = first.localeCompare(second, 'pt-BR', {
      sensitivity: 'base',
      numeric: true,
    });
    return order === 'asc' ? comparison : -comparison;
  });

const getRecordList = (payload: unknown): ProductBranchRecord[] => {
  if (Array.isArray(payload)) return payload as ProductBranchRecord[];
  if (!payload || typeof payload !== 'object') return [];
  const response = payload as Record<string, unknown>;
  if (Array.isArray(response.data))
    return response.data as ProductBranchRecord[];
  if (Array.isArray(response.body))
    return response.body as ProductBranchRecord[];
  return [];
};

export const SapProductBranchConfig = (): ReactElement => {
  const [records, setRecords] = useState<ProductBranchRecord[]>([]);
  const [currentRecord, setCurrentRecord] = useState<Record<string, unknown>>(
    {}
  );
  const [shouldShowEditPopup, setShouldShowEditPopup] = useState(false);
  const [shouldShowDeletePopup, setShouldShowDeletePopup] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [recordToDelete, setRecordToDelete] =
    useState<ProductBranchRecord | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [shouldShowImportModal, setShouldShowImportModal] = useState(false);
  const [selectedFilterField, setSelectedFilterField] =
    useState<SelectOption | null>(null);
  const [selectedFilterValues, setSelectedFilterValues] = useState<
    SelectOption[]
  >([]);
  const [order, setOrder] = useState<SortOrder>('asc');
  const [orderBy, setOrderBy] = useState<SortableColumn>('sap_code');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchRecords = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await listSapProductBranch();
      setRecords(getRecordList(response));
    } catch (error: unknown) {
      console.warn('Erro ao buscar sapProductBranch da API:', error);
      setRecords([]);
    } finally {
      setCurrentRecord({});
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  const showDeletePopup = (record: ProductBranchRecord): void => {
    setRecordToDelete(record);
    setDeleteError('');
    setShouldShowDeletePopup(true);
  };

  const showEditPopup = (record: ProductBranchRecord): void => {
    setCurrentRecord(record);
    setIsEditing(true);
    setShouldShowEditPopup(true);
  };

  const onAction = (): void => {
    setShouldShowEditPopup(false);
    setIsEditing(false);
    setCurrentRecord({});
    fetchRecords();
  };

  const deleteRecordAction = async (): Promise<void> => {
    if (!recordToDelete?.sap_code || recordToDelete?.branch_id === undefined) {
      return;
    }

    setIsDeleting(true);
    setDeleteError('');

    try {
      const resp = await delSapProductBranch(
        recordToDelete.sap_code,
        recordToDelete.branch_id
      );

      if (resp?.httpStatus && resp.httpStatus >= 400) {
        const msg =
          resp?.body?.message ||
          resp?.body?.error ||
          (typeof resp?.body === 'string' ? resp.body : null);
        throw new Error(
          msg || `Erro ao excluir registro (HTTP ${resp.httpStatus})`
        );
      }

      setShouldShowDeletePopup(false);
      setRecordToDelete(null);

      await fetchRecords();
    } catch (error: unknown) {
      console.warn('Erro ao deletar sapProductBranch:', error);
      setDeleteError(
        error instanceof Error
          ? error.message
          : 'Não foi possível excluir o registro'
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const selectedFilterOptions = useMemo<SelectOption[]>(() => {
    const field = selectedFilterField?.value as FilterField | undefined;
    if (!field) return [];

    return makeUniqueOptions(
      records.map((record) => {
        const value = formatCellValue(record[field]);
        return { value: normalizeText(value), label: value };
      })
    );
  }, [records, selectedFilterField]);

  const selectedFilterPlaceholder = selectedFilterField
    ? `Filtrar ${selectedFilterField.label.toLowerCase()}...`
    : 'Selecione um filtro primeiro';

  const filteredRecords = useMemo(() => {
    const field = selectedFilterField?.value as FilterField | undefined;
    const selectedValues = new Set(
      selectedFilterValues.map((option) => option.value)
    );
    if (!field || selectedValues.size === 0) return records;

    return records.filter((record) =>
      selectedValues.has(normalizeText(formatCellValue(record[field])))
    );
  }, [records, selectedFilterField, selectedFilterValues]);

  const sortedRecords = useMemo(
    () => sortRecords(filteredRecords, order, orderBy),
    [filteredRecords, order, orderBy]
  );
  const lastPage = Math.max(
    0,
    Math.ceil(sortedRecords.length / rowsPerPage) - 1
  );
  const currentPage = Math.min(page, lastPage);
  const visibleRecords = sortedRecords.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );

  const requestSort = (property: SortableColumn): void => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const EditPopup = () => (
    <SimplePopup
      width={950}
      height={600}
      zIndex={1000}
      outlet={
        <div
          style={{
            padding: '10px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '15px',
              borderBottom: '2px solid #e0e0e0',
              flexShrink: 0,
            }}
          >
            <div style={{ flex: 1 }}>
              <h2
                style={{
                  fontSize: '24px',
                  color: '#333',
                  fontWeight: 600,
                  margin: 0,
                  letterSpacing: '-0.5px',
                }}
              >
                {isEditing
                  ? 'Editar Produto por Filial'
                  : 'Novo Produto por Filial'}
              </h2>
              <p
                style={{
                  fontSize: '14px',
                  color: '#666',
                  margin: '5px 0 0 0',
                }}
              >
                {isEditing
                  ? 'Atualize as informações do vínculo de produto por filial'
                  : 'Preencha os dados para adicionar um novo vínculo de produto por filial'}
              </p>
            </div>
          </div>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <SapProductBranchForm
              sap_code={currentRecord.sap_code ?? ''}
              branch_id={currentRecord.branch_id ?? ''}
              layout={currentRecord.layout ?? ''}
              product_id={currentRecord.product_id ?? ''}
              isEditing={isEditing}
              onAction={onAction}
            />
          </div>
        </div>
      }
    />
  );

  const DeletePopup = () => (
    <SimplePopup
      width={420}
      height={250}
      zIndex={1000}
      outlet={
        <div style={{ display: 'block' }}>
          <p style={{ textAlign: 'center', fontWeight: '500' }}>
            Tem certeza que deseja excluir o vínculo &quot;
            {recordToDelete?.sap_code}/{recordToDelete?.branch_id}
            &quot;?
          </p>

          {deleteError ? (
            <p
              style={{
                color: '#b00020',
                marginTop: 10,
                marginBottom: 10,
                fontSize: 13,
                textAlign: 'center',
              }}
            >
              {deleteError}
            </p>
          ) : null}

          {isDeleting ? (
            <div
              style={{ width: '100%', textAlign: 'center', padding: '10px 0' }}
            >
              <Spinner />
            </div>
          ) : null}

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <SecondaryButton
              style={{ width: '100px' }}
              disabled={isDeleting}
              onClick={() => setShouldShowDeletePopup(false)}
            >
              Não
            </SecondaryButton>

            <PrimaryButton
              style={{ width: '100px' }}
              disabled={isDeleting}
              onClick={deleteRecordAction}
            >
              {isDeleting ? 'Excluindo...' : 'Sim'}
            </PrimaryButton>
          </div>
        </div>
      }
    />
  );

  return (
    <Content>
      {shouldShowEditPopup ? EditPopup() : <></>}
      {shouldShowDeletePopup ? DeletePopup() : <></>}
      {shouldShowImportModal ? (
        <SapProductBranchUploadModal
          onClose={() => setShouldShowImportModal(false)}
          onSuccess={() => {
            setShouldShowImportModal(false);
            fetchRecords();
          }}
        />
      ) : null}

      <ToolBar>
        <FilterBar>
          <FilterBox>
            <FilterLabel>Filtro</FilterLabel>
            <ReactSelect<SelectOption, false>
              options={FILTER_FIELDS}
              value={selectedFilterField}
              onChange={(value: SingleValue<SelectOption>) => {
                setSelectedFilterField(value);
                setSelectedFilterValues([]);
                setPage(0);
              }}
              placeholder="Escolha o filtro..."
              styles={selectStyles}
              menuPortalTarget={document.body}
              isClearable
              noOptionsMessage={() => 'Nenhum filtro encontrado'}
            />
          </FilterBox>
          <FilterBox className="filter-value">
            <FilterLabel>Valor</FilterLabel>
            <ReactSelect<SelectOption, true>
              isMulti
              isDisabled={!selectedFilterField}
              options={selectedFilterOptions}
              value={selectedFilterValues}
              onChange={(value: MultiValue<SelectOption>) => {
                setSelectedFilterValues(Array.from(value));
                setPage(0);
              }}
              placeholder={selectedFilterPlaceholder}
              styles={selectStyles}
              menuPortalTarget={document.body}
              noOptionsMessage={() => 'Nenhum valor encontrado'}
            />
          </FilterBox>
        </FilterBar>
        <div style={{ display: 'flex', gap: '10px', marginLeft: 'auto' }}>
          <SecondaryButton
            disabled={isLoading}
            onClick={() => setShouldShowImportModal(true)}
          >
            Importar planilha
          </SecondaryButton>

          <PrimaryButton
            disabled={isLoading}
            style={{
              opacity: isLoading ? 0.7 : 1,
              cursor: isLoading ? 'not-allowed' : 'pointer',
            }}
            onClick={() => {
              setIsEditing(false);
              setCurrentRecord({});
              setShouldShowEditPopup(true);
            }}
          >
            Novo vínculo
          </PrimaryButton>
        </div>
      </ToolBar>

      <TableWrapper>
        {isLoading ? (
          <Box sx={{ width: '100%', textAlign: 'center', py: 2.5 }}>
            <Spinner />
          </Box>
        ) : (
          <>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                border: '1px solid #e6e6e6',
                borderRadius: 1,
              }}
            >
              <MuiTable
                stickyHeader
                size="small"
                aria-label="Gerenciamento de produtos"
              >
                <TableHead>
                  <TableRow>
                    {TABLE_COLUMNS.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        sx={{
                          minWidth: column.minWidth,
                          backgroundColor: DarkGreen,
                          color: 'white',
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {column.sortable ? (
                          <TableSortLabel
                            active={orderBy === column.id}
                            direction={orderBy === column.id ? order : 'asc'}
                            onClick={() =>
                              requestSort(column.id as SortableColumn)
                            }
                            sx={{
                              color: 'white !important',
                              '& .MuiTableSortLabel-icon': {
                                color: 'white !important',
                              },
                            }}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRecords.map((record) => (
                    <TableRow
                      hover
                      key={`${record.sap_code}-${record.branch_id}`}
                    >
                      <TableCell align="center">
                        <Stack
                          direction="row"
                          justifyContent="center"
                          spacing={0.25}
                        >
                          <Tooltip title="Excluir vínculo">
                            <IconButton
                              size="small"
                              aria-label={`Excluir vínculo ${
                                record.sap_code ?? ''
                              }/${record.branch_id ?? ''}`}
                              onClick={() => showDeletePopup(record)}
                            >
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editar vínculo">
                            <IconButton
                              size="small"
                              aria-label={`Editar vínculo ${
                                record.sap_code ?? ''
                              }/${record.branch_id ?? ''}`}
                              onClick={() => showEditPopup(record)}
                            >
                              <EditOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                      <TableCell>{formatCellValue(record.sap_code)}</TableCell>
                      <TableCell>{formatCellValue(record.branch_id)}</TableCell>
                      <TableCell>
                        {formatCellValue(record.branch_description)}
                      </TableCell>
                      <TableCell>{formatCellValue(record.layout)}</TableCell>
                      <TableCell>
                        {formatCellValue(record.product_id)}
                      </TableCell>
                      <TableCell>
                        {formatCellValue(record.product_description)}
                      </TableCell>
                      <TableCell>
                        {formatCellValue(record.product_generic)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {visibleRecords.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={TABLE_COLUMNS.length}
                        align="center"
                        sx={{ py: 5 }}
                      >
                        Nenhum vínculo encontrado.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </MuiTable>
            </TableContainer>
            <TablePagination
              component="div"
              count={sortedRecords.length}
              page={currentPage}
              onPageChange={(_, nextPage) => setPage(nextPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Linhas por página"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} de ${count}`
              }
              sx={{ flexShrink: 0, overflow: 'hidden' }}
            />
          </>
        )}
      </TableWrapper>
    </Content>
  );
};
